/* eslint-disable class-methods-use-this */
/* eslint-disable prefer-promise-reject-errors */

import { shellIncidentReporter } from '../../../features/shell/logging/shellIncidentReporter';

const MESSAGE_ACTION_SUCCEEDED = 'ACTION_SUCCEEDED';
const MESSAGE_ACTION_FAILED = 'ACTION_FAILED';

const COALESCED_READ_ACTIONS = new Set([
  'app.bootstrap',
  'gateway.identity.get',
  'onboarding.state',
]);

const inFlightReadsByParent = new WeakMap();

export const BRIDGE_ERROR_CODES = {
  NOT_IN_IFRAME: 'NOT_IN_IFRAME',
  UNTRUSTED_PARENT: 'UNTRUSTED_PARENT',
  TIMEOUT: 'TIMEOUT',
};

export function trustedParentOrigin(referrer = (typeof document !== 'undefined' ? document.referrer : '')) {
  try {
    const url = new URL(referrer);
    return (url.protocol === 'https:' || url.protocol === 'http:') ? url.origin : null;
  } catch (_error) {
    return null;
  }
}

export class BridgeClient {
  constructor({ parentWindow, parentOrigin } = {}) {
    this.parentWindow = parentWindow || (typeof window !== 'undefined' ? window.parent : null);
    this.parentOrigin = parentOrigin || trustedParentOrigin();
  }

  async forceRegister(payload) {
    return this.sendMessageWithResponse('REGISTER', payload);
  }

  async setTokens(token, secret, shopId) {
    return this.sendMessageWithResponse('SET_TOKENS', { token, secret, shopId });
  }

  async clearTokens() {
    return this.sendMessageWithResponse('CLEAR_TOKENS');
  }

  async getDetails() {
    return this.sendMessageWithResponse('GET_DETAILS');
  }

  async forceReloadPage() {
    return this.sendMessageWithResponse('RELOAD_PAGE');
  }

  async openBillingPage() {
    return this.sendMessageWithResponse('OPEN_BILLING');
  }

  async openThemeEditor(payload = {}) {
    return this.sendMessageWithResponse('OPEN_THEME_EDITOR', payload);
  }

  async verifyThemeEmbed(payload = {}) {
    return this.sendMessageWithResponse('VERIFY_SCRIPT', payload);
  }

  async getFinalUrl(url) {
    return this.sendMessageWithResponse('GET_FINAL_URL', { url }, 10000);
  }

  async logout() {
    return this.sendMessageWithResponse('LOGOUT', {}, 10000);
  }

  async getDiagnosticsSummary(params = {}) {
    return this.sendMessageWithResponse('diagnostics.events.summary', params);
  }

  async getDiagnosticsEvents(params = {}) {
    return this.sendMessageWithResponse('diagnostics.events.list', params);
  }

  async getDiagnosticsSnapshot() {
    return this.sendMessageWithResponse('diagnostics.snapshot.get', {});
  }

  sendMessageWithResponse(type, payload = {}, timeout = 60000) {
    if (!COALESCED_READ_ACTIONS.has(type)
      || !this.parentWindow
      || (typeof this.parentWindow !== 'object' && typeof this.parentWindow !== 'function')
    ) {
      return this.sendBridgeRequest(type, payload, timeout);
    }

    let inFlightReads = inFlightReadsByParent.get(this.parentWindow);
    if (!inFlightReads) {
      inFlightReads = new Map();
      inFlightReadsByParent.set(this.parentWindow, inFlightReads);
    }

    const requestKey = `${this.parentOrigin || ''}:${type}:${timeout}:${JSON.stringify(payload)}`;
    const inFlight = inFlightReads.get(requestKey);
    if (inFlight) return inFlight;

    const request = this.sendBridgeRequest(type, payload, timeout);
    inFlightReads.set(requestKey, request);

    const clearRequest = () => {
      if (inFlightReads.get(requestKey) === request) {
        inFlightReads.delete(requestKey);
      }
    };
    request.then(clearRequest, clearRequest);

    return request;
  }

  sendBridgeRequest(type, payload = {}, timeout = 60000) {
    const requestId = `${type}-${Math.random().toString(36).substring(7)}`;
    let timeoutId;
    let handleReceivedMessage;

    const promise = new Promise((resolve, reject) => {
      if (!this.parentWindow || this.parentWindow === window) {
        shellIncidentReporter.report('bridge.error', {
          flowId: null,
          shopId: null,
          route: null,
          stage: 'bridge',
          errorCode: BRIDGE_ERROR_CODES.NOT_IN_IFRAME,
          status: 'error',
          durationMs: null,
        });
        reject({ result: 'error', errorCode: BRIDGE_ERROR_CODES.NOT_IN_IFRAME });
        return;
      }
      if (!this.parentOrigin) {
        shellIncidentReporter.report('bridge.error', {
          flowId: null,
          shopId: null,
          route: null,
          stage: 'bridge',
          errorCode: BRIDGE_ERROR_CODES.UNTRUSTED_PARENT,
          status: 'error',
          durationMs: null,
        });
        reject({ result: 'error', errorCode: BRIDGE_ERROR_CODES.UNTRUSTED_PARENT });
        return;
      }
      handleReceivedMessage = (event) => {
        const { data } = event;
        if (event.origin !== this.parentOrigin
          || event.source !== this.parentWindow
          || !data
          || typeof data !== 'object'
          || data.requestId !== requestId
          || ![MESSAGE_ACTION_SUCCEEDED, MESSAGE_ACTION_FAILED].includes(data.type)
        ) {
          return;
        }
        if (data.requestId === requestId) {
          // Cleanup synchronously before settling so no dangling promise is created
          // by attaching .finally() on the returned promise.
          clearTimeout(timeoutId);
          window.removeEventListener('message', handleReceivedMessage);
          if (data.type === MESSAGE_ACTION_SUCCEEDED) {
            resolve(data.payload || {});
          } else if (data.type === MESSAGE_ACTION_FAILED) {
            reject(data.payload || { error: 'Unknown error' });
          }
        }
      };
      window.addEventListener('message', handleReceivedMessage);
      this.parentWindow.postMessage({ type, requestId, payload }, this.parentOrigin);

      timeoutId = setTimeout(() => {
        window.removeEventListener('message', handleReceivedMessage);
        shellIncidentReporter.report('bridge.timeout', {
          flowId: null,
          shopId: null,
          route: null,
          stage: 'bridge',
          errorCode: BRIDGE_ERROR_CODES.TIMEOUT,
          status: 'timeout',
          durationMs: timeout,
        });
        reject({ result: 'error', errorCode: BRIDGE_ERROR_CODES.TIMEOUT });
      }, timeout);
    });

    return promise;
  }
}
